// Libs
import classNames from 'classnames/bind';
import { Button, Grid2, InputLabel, Stack, Switch } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseSelect } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBaseOption } from '~/utils/interface/common';
import { mockDataJobType, mockDataUnitJob } from '~/utils/mockData/job';
import { TypeTabCreateEstimateEnum } from '~/utils/enum/common';
import { IFormEstimate } from '~/utils/interface/estimate';
// Styles, images, icons
import styles from './CreateEstimateFieldsTab.module.scss';

type Props = {
  onEdit?: (data: IFormEstimate, type: string) => void;
  onCancelEdit: () => void;
};

const cx = classNames.bind(styles);

const CreateEstimateFieldsTab = (props: Props) => {
  //#region Destructuring Props
  const { onEdit, onCancelEdit } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/estimate', 'common/base']);
  const { id: estimateId } = useParams();
  const {
    control,
    getValues,
    formState: { errors, isDirty },
  } = useFormContext<IFormEstimate>();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitFormFields = () => {
    const dataForm = getValues();
    const dataBody: IFormEstimate = {
      clientId: EMPTY_STRING,
      type: dataForm?.type || EMPTY_STRING,
      unit: dataForm?.unit || EMPTY_STRING,
      callback: dataForm?.callback || false,
    };

    onEdit && onEdit(dataBody, TypeTabCreateEstimateEnum.FIELDS);
  };
  //#endregion Handle Function

  return (
    <Grid2
      component={'div'}
      id='estimateFieldsComponent'
      className={cx('container')}
      container
      rowSpacing={'12px'}
      columnSpacing={'12px'}
    >
      <Grid2 size={5}>
        <Controller
          name='type'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseSelect
              label={t('field_estimate_fields_type_label')}
              value={value || EMPTY_STRING}
              onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
              options={mockDataJobType}
              errorMessage={errors.type?.message}
            />
          )}
        />
      </Grid2>

      <Grid2 size={5}>
        <Controller
          name='unit'
          control={control}
          render={({ field: { value, onChange } }) => (
            <BaseSelect
              label={t('field_estimate_fields_unit_label')}
              value={value || EMPTY_STRING}
              onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
              options={mockDataUnitJob}
              errorMessage={errors?.unit?.message}
            />
          )}
        />
      </Grid2>

      <Grid2 size={'grow'}>
        <InputLabel className={cx('label')}>{t('field_estimate_fields_callback_label')}</InputLabel>
        <Controller
          name='callback'
          control={control}
          render={({ field: { value, onChange } }) => (
            <Switch checked={value} onChange={(e) => onChange(e.target.checked)} size='medium' />
          )}
        />
      </Grid2>

      {estimateId && (
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={onCancelEdit}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='button' onClick={handleSubmitFormFields} disabled={!isDirty}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>
      )}
    </Grid2>
  );
};

export default CreateEstimateFieldsTab;
